import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Edit, GripVertical, Info, Trash } from "lucide-react";
import { AddOrderBumpSheet } from "./AddOrderBumpSheet";
import useModalStore from "@/store/useModalStore";
import CustomSheet from "@/components/shared/Sheet/Sheet.tsx";
import { OrderBump, useOrderBumps } from "../hooks/useOrderBumps.ts";
import { SecretOrderBumpInfoSheet } from "@/pages/EditProduct/Orderbumps/Partials/SecretOrderBumpInfoSheet";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom.tsx";
import { OrderBumps } from "@/assets/icons/OrderBumps.tsx";
import { EditOrderBumpSheet } from "./EditOrderBumpSheet.tsx";
import { TableCell, TableRow } from "@/components/ui/table.tsx";
import CTATable from "@/components/shared/TableListCustom/CTATable.tsx";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete.tsx";
import { ProductDetailsResponse, ProductRole } from "@/@types/Products.ts";
import { useOutletContext } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePermissions } from "@/auth/PermissionsContext";
import { recurringIntervalSuffix } from "@/utils/formatOfferRecurringLabel";

interface OrderBumpListProps {
  productId: string;
}

export default function OrderBumpList({ productId }: OrderBumpListProps) {
  const { t } = useTranslation("orderBumps");
  const { openModal, modals, closeModal } = useModalStore();
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();
  const [orderBump, setOrderBump] = useState<OrderBump>();

  const { hasPermission } = usePermissions();
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const canCreate = isProducer && hasPermission('order_bumps.create_order_bump');
  const canEdit = isProducer && hasPermission('order_bumps.edit_order_bump');
  const canDelete = isProducer && hasPermission('order_bumps.delete_order_bump');
  const canToggleSecret = isProducer && hasPermission('order_bumps.toggle_secret_order_bump');

  const [isSecretOrderBumpEnabled, setIsSecretOrderBumpEnabled] = useState(
    product?.secret_order_bump
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const queryClient = useQueryClient();

  const {
    orderBumps,
    createOrderBump,
    activateSecretOrderBump,
    isCreating,
    isLoading,
    deleteOrderBump,
  } = useOrderBumps({ productId });

  const submitSecret = async (checked: boolean) => {
    if (isSubmitting) return;

    const payload = {
      secret_order_bump: checked,
    };
    setIsSubmitting(true);
    try {
      await activateSecretOrderBump(payload);
      setIsSecretOrderBumpEnabled(checked);
      queryClient.invalidateQueries({
        queryKey: [`get-product-${productId}`],
      });
    } catch (error) {
      console.error("Error activating secret order bump:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateOrderBump = async (formData: any) => {
    const data = {
      productId: formData.productId,
      offer_id: formData.offer_id,
      display_text: formData.display_text,
      display_description: formData.display_description,
      call_to_action: formData.call_to_action,
    };

    try {
      await createOrderBump(data);
    } catch (error) {
      console.error("Error creating order bump:", error);
      throw error;
    }
  };

  const headers = [
    // { title: t('order'), key: 'order' },
    { title: t("product"), key: "product" },
    { title: t("offer"), key: "offer" },
    { title: "", key: "actions" },
  ];

  return (
    <Card className="w-full mt-8">
      <div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 ">
          <div className=" w-full">
            <div className="flex md:flex-row flex-col justify-between gap-3 md:items-center p-4">
              <h1 className="text-base font-semibold">{t("order_bumps")}</h1>
              {canCreate && (
                <Button
                  onClick={() => openModal("add-order-bump")}
                  className="bg-primary text-black hover:bg-primary/90"
                >
                  + {t("add_order_bump")}
                </Button>
              )}
            </div>
            {canToggleSecret && (orderBumps?.data?.length ?? 0) > 0 && (
                <div className="h-[64px] flex border-t border-t-colorBorder -mb-4 items-center justify-between">
                  <div className="flex items-center gap-2 p-2">
                    <Switch
                      checked={isSecretOrderBumpEnabled}
                      onCheckedChange={submitSecret}
                    />
                    <span className="text-sm text-zinc-400">
                      {t("activate_secret_order_bump")}
                    </span>
                    <button
                      onClick={() => openModal("secret-order-bump-info")}
                      className="text-zinc-400 hover:text-zinc-100 transition-colors"
                    >
                      <Info size={16} />
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>

        <div className="mx-4 mb-4 flex gap-2 rounded-md border border-zinc-700 bg-zinc-900/50 p-3 text-sm text-zinc-400">
          <Info size={16} className="mt-0.5 shrink-0 text-zinc-500" />
          <p>{t("payment_type_homogeneity_notice")}</p>
        </div>

        <TableListCustom
          emptyDescriptionMessage={t("empty.description")}
          emptyIconMessage={<OrderBumps />}
          emptyTitleMessage={t("empty.title")}
          headers={headers}
          isLoadingData={isLoading}
          data={orderBumps ?? { data: [] }}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          {orderBumps?.data?.map((orderBump, index) => (
            <TableRow key={orderBump.id} className="hover:bg-transparent">
              {/* <TableCell className='whitespace-nowrap'>
                <div className='flex items-center gap-2'>
                  <GripVertical className='cursor-move text-gray-400' />
                  <span className='text-sm text-zinc-400'>{index + 1}</span>
                </div>
              </TableCell> */}
              <TableCell>
                <span className="text-sm">{orderBump.offer.product.name}</span>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {orderBump.offer.name}
                  </span>
                  <span className="text-sm text-zinc-400">
                    {orderBump.offer.price_formatted}
                    {recurringIntervalSuffix(
                      orderBump.offer.payment_type,
                      orderBump.offer.recurring_interval,
                    )}
                  </span>
                </div>
              </TableCell>
              {(canEdit || canDelete) && (
                <TableCell className="w-[50px]">
                  <CTATable
                    items={[
                      ...(canEdit ? [{
                        id: 1,
                        label: t("edit"),
                        icon: <Edit />,
                        action: () => {
                          setOrderBump(orderBump);
                          openModal("edit-order-bump");
                        },
                      }] : []),
                      ...(canDelete ? [{
                        id: 2,
                        label: t("delete"),
                        icon: <Trash color="#F87171" />,
                        action: () => {
                          setOrderBump(orderBump);
                          openModal("delete-orderBump");
                        },
                      }] : []),
                    ]}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableListCustom>
      </div>
      {canDelete && (
        <DialogDelete
          isOpen={modals["delete-orderBump"]}
          onClose={() => closeModal("delete-orderBump")}
          title={t("delete_order_bump_title")}
          description={t("delete_order_bump_description")}
          onDelete={() => {
            if (orderBump?.id) {
              deleteOrderBump(orderBump.id);
            }
          }}
        />
      )}
      {canCreate && (
        <CustomSheet
          open={modals["add-order-bump"]}
          onClose={() => closeModal("add-order-bump")}
        >
          <AddOrderBumpSheet
            isPending={isCreating}
            onAdd={handleCreateOrderBump}
            productId={productId}
            modalName="add-order-bump"
          />
        </CustomSheet>
      )}

      {canToggleSecret && (
        <CustomSheet
          open={modals["secret-order-bump-info"]}
          onClose={() => closeModal("secret-order-bump-info")}
        >
          <SecretOrderBumpInfoSheet
            isEnabled={isSecretOrderBumpEnabled}
            onToggle={submitSecret}
            onClose={() => closeModal("secret-order-bump-info")}
            isSubmitting={isSubmitting}
          />
        </CustomSheet>
      )}

      {canEdit && orderBump && (
        <CustomSheet
          open={modals["edit-order-bump"]}
          onClose={() => closeModal("edit-order-bump")}
        >
          <EditOrderBumpSheet
            productId={productId}
            orderBump={orderBump}
            modalName="edit-order-bump"
          />
        </CustomSheet>
      )}
    </Card>
  );
}
